import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, MessageCircle, Gift, BadgeCheck, MoreHorizontal, Flag, Pencil, Trash2, Globe, Users, Lock } from "lucide-react";
import PostOwnerEditDialog from "@/components/PostOwnerEditDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import ReportModal from "@/components/ReportModal";
import GiftPicker from "@/components/GiftPicker";
import CommentsSheet from "@/components/CommentsSheet";
import VipBadge from "@/components/VipBadge";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";
import TopBadge from "@/components/TopBadge";
import useAvatarPeek from "@/hooks/useAvatarPeek";
import UserBadge from "@/components/UserBadge";
import { useControlCenter } from "@/context/ControlCenterContext";
import { Shield, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { requestVipCheck } from "@/components/vip/VipAwardAnnouncer";
import { FEED } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function PostCard({ post, onChange, onChanged }) {
  const { user } = useAuth();
  const cc = useControlCenter();
  const peek = useAvatarPeek();
  const [liked, setLiked] = useState(!!post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [gifts, setGifts] = useState(post.gifts_count);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [local, setLocal] = useState({ text: post.text, visibility: post.visibility || "public" });
  const isMine = !!(user && post.author && user.id === post.author.id);
  const deleteMine = async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      setRemoved(true); toast.success("Post deleted."); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };
  const VisIcon = local.visibility === "private" ? Lock : local.visibility === "followers" ? Users : Globe;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [bump, setBump] = useState(false);
  if (removed) return null;

  const toggleLike = async () => {
    if (!user) return toast.error("Please log in to react.");
    if (busy) return;
    setBusy(true);
    const prev = { liked, likes };
    setLiked(!liked);
    setLikes(likes + (liked ? -1 : 1));
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(data.likes_count);
    } catch (e) {
      setLiked(prev.liked);
      setLikes(prev.likes);
    } finally {
      setBusy(false);
    }
  };

  const openGift = () => {
    if (!user) return toast.error("Please log in to send a gift.");
    setGiftOpen(true);
  };

  const sendGift = async (giftKey) => {
    const { data } = await api.post(`/posts/${post.id}/gift`, { gift_key: giftKey });
    requestVipCheck();
    setGifts(data.gifts_count);
    setBump(true);
    setTimeout(() => setBump(false), 700);
    onChange && onChange();
  };

  return (
    <article
      data-post-id={post.id}
      data-testid={FEED.post(post.id)}
      className="card-surface p-6 hover-lift"
    >
      <div className="flex items-center gap-3 mb-4">
        <UserBadge
          userRef={post.author.id || post.author.handle}
          href={`/u/${post.author.id}`}
          className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/60"
          data-testid={`post-author-peek-${post.id}`}
          aria-label={`View ${post.author.display_name}'s profile`}
        >
          <Avatar className={`w-11 h-11 border border-white/10`}>
            <AvatarImage src={post.author.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-sm">
              {post.author.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </UserBadge>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <UserBadge
              userRef={post.author.id || post.author.handle}
              href={`/u/${post.author.id}`}
              data-testid={`post-author-name-peek-${post.id}`}
              className={`text-sm font-medium hover:text-rose-500 transition-colors truncate text-left`}
            >
              {post.author.display_name}
            </UserBadge>
            {post.author.is_creator && (
              <BadgeCheck size={14} className="text-rose-500 shrink-0" />
            )}
            <LifetimeBossBadge show={post.author.lifetime_boss} size="xs" />
            <VipBadge tier={post.author.vip_tier} size="xs" />
            <TopBadge show={post.author.top_badge} size="xs" />
          </div>
          <div className="text-xs text-zinc-500">
            {!post.author.handle_hidden && post.author.handle && (
              <>
                @<span className="font-medium">{post.author.handle}</span>
                {" · "}
              </>
            )}
            {timeAgo(post.created_at)}
            {(post.shared_desires_count || 0) >= 1 && (
              <span
                className="ml-2 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-950/40 px-1.5 py-0.5 text-[10px] text-rose-300"
                title={`${post.shared_desires_count} shared desires`}
                data-testid={`post-shared-desires-${post.id}`}
              >
                ✨ {post.shared_desires_count}
              </span>
            )}
            {post.community && (
              <>
                {" · "}
                <span className="text-rose-500">{post.community.name}</span>
              </>
            )}
          </div>
        </div>
        {isMine && (
          <div className="flex items-center gap-1">
            <span data-testid={`post-visibility-${post.id}`} title={`Visible to: ${local.visibility}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 px-1.5"><VisIcon size={10} /> {local.visibility === "private" ? "Only me" : local.visibility}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`post-owner-menu-${post.id}`} className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors" aria-label="Manage post"><MoreHorizontal size={18} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 rounded-none">
                <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid={`post-edit-${post.id}`} className="focus:bg-white/5 rounded-none cursor-pointer"><Pencil size={14} className="mr-2" /> Edit caption &amp; privacy</DropdownMenuItem>
                <DropdownMenuItem onClick={deleteMine} data-testid={`post-delete-${post.id}`} className="text-rose-500 focus:text-rose-400 focus:bg-white/5 rounded-none cursor-pointer"><Trash2 size={14} className="mr-2" /> Delete post</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PostOwnerEditDialog open={editOpen} onOpenChange={setEditOpen} post={{ ...post, ...local }} onSaved={(p) => { setLocal({ text: p.text, visibility: p.visibility }); onChanged?.(); }} />
          </div>
        )}
        {user && user.id !== post.author.id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`post-menu-${post.id}`}
                className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition-colors"
                aria-label="Post options"
              >
                <MoreHorizontal size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 rounded-none">
              <DropdownMenuItem
                onClick={() => setReportOpen(true)}
                data-testid={`post-report-${post.id}`}
                className="text-rose-500 focus:text-rose-400 focus:bg-white/5 rounded-none cursor-pointer"
              >
                <Flag size={14} className="mr-2" /> Report post
              </DropdownMenuItem>
              {cc.isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => cc.openCC({ tab: "content", target: { type: "post", id: post.id } })} data-testid={`post-moderate-${post.id}`} className="text-sky-300 focus:bg-white/5 rounded-none cursor-pointer"><Shield size={14} className="mr-2" /> Moderate</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => cc.openCC({ tab: "content", target: { type: "post", id: post.id, evidence: true } })} data-testid={`post-capture-evidence-${post.id}`} className="text-sky-300 focus:bg-white/5 rounded-none cursor-pointer"><Camera size={14} className="mr-2" /> Capture evidence</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="text-[15px] text-zinc-100 leading-relaxed whitespace-pre-wrap break-words">
        {isMine ? local.text : post.text}
      </div>

      {post.media_url && (post.media_type === "video" || /\.(mp4|webm|mov)(\?|$)/i.test(post.media_url) ? (
        <video
          data-testid={`post-video-${post.id}`}
          src={post.media_url}
          controls
          playsInline
          preload="metadata"
          className="mt-4 w-full max-h-[520px] bg-black border border-white/5"
        />
      ) : (
        <img
          src={post.media_url}
          alt=""
          loading="lazy"
          className="mt-4 w-full max-h-[520px] object-cover border border-white/5"
        />
      ))}

      <div className="flex items-center gap-6 mt-5 pt-4 border-t border-white/5">
        <button
          data-testid={FEED.likeButton(post.id)}
          onClick={toggleLike}
          className={`flex items-center gap-2 text-sm ${
            liked ? "text-rose-500" : "text-zinc-400 hover:text-rose-500"
          } transition-colors`}
        >
          <Heart size={18} fill={liked ? "currentColor" : "none"} strokeWidth={1.5} />
          <span>{likes}</span>
        </button>
        <button
          data-testid={FEED.commentButton(post.id)}
          onClick={() => {
            if (!user) return toast.error("Please log in to reply.");
            setCommentsOpen(true);
          }}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
        >
          <MessageCircle size={18} strokeWidth={1.5} />
          <span>{commentsCount}</span>
        </button>
        <button
          data-testid={FEED.giftButton(post.id)}
          onClick={openGift}
          disabled={user && user.id === post.author.id}
          className={`flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 transition-transform ml-auto ${
            bump ? "scale-125 text-amber-400" : "scale-100"
          } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-zinc-400`}
          title={user && user.id === post.author.id ? "You can't gift yourself" : "Send a gift"}
        >
          <Gift size={18} strokeWidth={1.5} />
          <span>{gifts}</span>
        </button>
      </div>
      <ReportModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        target={{ type: "post", id: post.id }}
      />
      <GiftPicker
        open={giftOpen}
        onOpenChange={setGiftOpen}
        onSend={sendGift}
        context="post"
        recipientName={post.author.display_name}
      />
      <CommentsSheet
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        post={post}
        onChange={(newCount) => {
          setCommentsCount(newCount);
          onChange && onChange();
        }}
      />
      {peek.node}
    </article>
  );
}
